import {setApplePaySessionStarted, stopOrderProcessing} from '../../actions/site-actions';
import {setApplePayPaymentDetails, validateApplepayMerchant} from '../../actions/APIActions/CheckoutAPIActions';
import {placeOrderViaApiPoller} from '../../api/pollers/pollers';

import {getOrderTotalNumeric} from '../../selectors/OrderSummarySelectors';
import getApplepaySupportedNetworks from '../../selectors/getApplepaySupportedNetworks';
import store from '../../store/store';
import Track from '../../utils/tracking';
import doMetric from '../../tracking/bigQueryMetricRecorder';

export function isApplePayAvailable() {
  return window.ApplePaySession && window.ApplePaySession.canMakePayments();
}

export function abortApplePaySession() {
  try {
    // Abort is not supported in all versions of ApplePay API
    window.applePayPaymentSession.abort();
  } catch (error) {
    Track.track(error);
  }
  delete window.applePayPaymentSession;
}

export function beginApplePaySession() {
  const session = window.applePayPaymentSession;
  try {
    store.dispatch(setApplePaySessionStarted(true));
    session.oncancel = (event) => {
      Track.error(`ApplePay triggered cancel, ${JSON.stringify(event)}`);
      
      try {
        doMetric('applepayCheckout', [{name: 'cancelled', value: true}], [], 'impression');
      } catch (e) {
        Track.error(e);
      }
      
      abortApplePaySession();
      store.dispatch(setApplePaySessionStarted(false));
      store.dispatch(stopOrderProcessing());
      placeOrderViaApiPoller.stopPoller();
    };
    session.begin();
  } catch (error) {
    Track.track(error);
  }
}

export function createApplePaySession() {
  const createPaymentRequest = _createApplePayPaymentRequest();
  
  const applePayPaymentSession = new ApplePaySession(_getSupportedBrowserVersion(), createPaymentRequest);
  
  applePayPaymentSession.onvalidatemerchant = (event) => {
    _validateMerchant(event)
      .then((validationResponse) => {
        applePayPaymentSession.completeMerchantValidation(validationResponse);
      })
      .catch((event) => {
        Track.track(event);
      });
  };
  
  applePayPaymentSession.onpaymentauthorized = (event) => {
    _setApplePayPaymentDetails(event.payment.token.paymentData)
      .then(placeOrderViaApiPoller.runPoller)
      .catch((event) => {
        Track.track(event);
      });
  };
  
  window.applePayPaymentSession = applePayPaymentSession;
}

export function completeApplePayPayment() {
  window.applePayPaymentSession.completePayment(ApplePaySession.STATUS_SUCCESS);
}

export function _createApplePayPaymentRequest() {
  const state = store.getState();
  const orderTotal = getOrderTotalNumeric(state);
  const {currencyCode} = state.start.orderTotal;
  const {siteName} = state.start.siteDataV2;
  
  return {
    countryCode: 'GB', // This is where the payment will be processed. Not the customer address.
    currencyCode,
    supportedNetworks: getApplepaySupportedNetworks(state),
    merchantCapabilities: ['supports3DS'],
    total: {label: siteName, amount: orderTotal},
  };
}

function _getSupportedBrowserVersion() {
  const versions = [6, 5, 4, 3, 2, 1]; // Must be in order!
  return versions.find(window.ApplePaySession.supportsVersion);
}

function _validateMerchant(validateEvent) {
  const data = {
    validationUrl: validateEvent.validationURL,
    domain: window.location.hostname,
  };
  
  return store.dispatch(validateApplepayMerchant(data))
    .then(merchantResponse => (merchantResponse?.msg ? merchantResponse.msg : {}))
    .catch(error => Track.error(`Failed to validate merchant for applepay, ${error}`));
}

function _setApplePayPaymentDetails(paymentDetails) {
  const session = window.applePayPaymentSession;
  if (window.start.settings.applepay_block_cancel_if_payment_details_called) {
    session.oncancel = (() => {
      try {
        doMetric('applepayCheckout', [{name: 'cancelled', value: 'blocked'}], [], 'impression');
      } catch (e) {
        Track.error(e);
      }
    });
  }
  
  const paymentDetailsRequest = {
    encryptedAppleToken: paymentDetails.data,
    signature: paymentDetails.signature,
    version: paymentDetails.version,
    applePayHeader: paymentDetails.header,
  };
  
  return store.dispatch(setApplePayPaymentDetails(paymentDetailsRequest))
    .then(merchantResponse => (merchantResponse?.msg ? merchantResponse.msg : {}))
    .catch(error => Track.error(`Failed to set applepay details, ${error}`));
}
